/**
 * 控制台唯一布局壳（总纲 §7-1）。
 *
 *   - 桌面：左侧 232px，**今天 · 员工 · 客户 · 费用** 四项 + 底部齿轮「设置」+ 余额胶囊
 *   - 顶栏 48px：铃铛（未读数）· 帮助「?」· 企业切换 · 用户
 *   - ≤ 960px：侧栏收起，底部 Tab（四项 + 「更多」：设置 · 消息 · 帮助）
 *
 * 布局样式在 ./shell.css；颜色 / 圆角 / 字沿用 tokens.css；选中态仍是「主色 10% 底 + 主色文字」（spec/08 §2.2.3）。
 * 权限不够的项不渲染（Skill shell.md）。
 */
import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'antd';
import { BellOutlined, EllipsisOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { NAV_SETTINGS, showSettings, visibleMain, type NavItem } from './navItems';
import { TenantSwitch, UserMenu } from './UserMenu';
import { useAuth } from '@/stores/auth';
import { can } from '@/lib/permission';
import { money } from '@/lib/format';
import { useAccount } from '@/hooks/useFinance';
import { getUnreadCount } from '@/api/endpoints/system';
import { qk } from '@/api/query';
import { CONSOLE_HOME } from '@/lib/paths';
import { BRAND_NAME } from '@/lib/brand';
import './shell.css';

function SideLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.exact ?? false}
      className={({ isActive }) => `shell-link${isActive ? ' is-active' : ''}`}
    >
      <Icon />
      <span>{item.label}</span>
    </NavLink>
  );
}

/** 手机底部第五格：设置 · 消息 · 帮助收进一个菜单，四项主导航不挤。 */
function MoreTab({ gear, unread }: { gear: boolean; unread: number }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const GearIcon = NAV_SETTINGS.icon;
  const unreadText = unread > 99 ? '99+' : String(unread);
  const items = [
    ...(gear ? [{ key: NAV_SETTINGS.to, icon: <GearIcon />, label: NAV_SETTINGS.label }] : []),
    {
      key: '/inbox',
      icon: <BellOutlined />,
      label: unread > 0 ? `消息（${unreadText} 条未读）` : '消息',
    },
    { key: '/help', icon: <QuestionCircleOutlined />, label: '帮助' },
  ];
  const active = items.some((i) => pathname === i.key || pathname.startsWith(`${i.key}/`));
  return (
    <Dropdown
      trigger={['click']}
      placement="topRight"
      menu={{
        items,
        onClick: ({ key }) => {
          void navigate(key);
        },
      }}
    >
      <button
        type="button"
        className={`cshell-tab${active ? ' is-active' : ''}`}
        aria-label={unread > 0 ? `更多，消息 ${unreadText} 条未读` : '更多'}
      >
        <EllipsisOutlined />
        <span>更多</span>
        {unread > 0 ? <span className="cshell-tab-dot" aria-hidden="true" /> : null}
      </button>
    </Dropdown>
  );
}

function TabLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.exact ?? false}
      className={({ isActive }) => `cshell-tab${isActive ? ' is-active' : ''}`}
    >
      <Icon />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppShell() {
  const me = useAuth((s) => s.me);
  const roles = me?.roles;
  const main = visibleMain(roles);
  const gear = showSettings(roles);
  const seeBilling = can(roles, 'finance:read');
  const account = useAccount();
  const tenantName = me?.tenant.name ?? '';

  // 侧栏余额是「还能花的钱」（现金 + 通话额度池未花掉的部分），与费用页同一口径
  const balance = account.data?.available_micro ?? null;
  const arrear = balance !== null && balance <= 0;

  const unreadQ = useQuery({
    queryKey: qk.system.unread(),
    queryFn: ({ signal }) => getUnreadCount(signal),
    enabled: Boolean(me),
    staleTime: 30_000,
  });
  const unreadRaw = unreadQ.data?.count;
  const unread =
    typeof unreadRaw === 'number' && Number.isFinite(unreadRaw) && unreadRaw > 0
      ? unreadRaw
      : 0;

  useEffect(() => {
    document.title = `${BRAND_NAME} 管理平台`;
    // index.html 静态默认 index（官网要被搜到）；进了控制台壳子就翻成 noindex，离开还回去
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    robots?.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (robots && prevRobots) robots.setAttribute('content', prevRobots);
    };
  }, []);

  return (
    <div className="cshell">
      <nav className="cshell-side" aria-label="主导航">
        <Link to={CONSOLE_HOME} className="shell-brand">
          <span className="shell-brand-name">{BRAND_NAME}</span>
          <span className="shell-brand-meta">{tenantName}</span>
        </Link>
        {main.map((item) => (
          <SideLink key={item.to} item={item} />
        ))}
        <div className="cshell-side-spacer" />
        {gear ? <SideLink item={NAV_SETTINGS} /> : null}
        <div className="cshell-side-foot">
          <div className="shell-capsules">
            {/* 看不了费用的角色只显示，不给一个点进去就被送回今天的入口 */}
            {seeBilling ? (
              <Link
                to="/billing"
                className={`shell-capsule${arrear ? ' is-warn' : ''}`}
                aria-label={`余额 ${money(balance)}`}
              >
                余额 {money(balance)}
              </Link>
            ) : (
              <span className="shell-capsule">余额 {money(balance)}</span>
            )}
          </div>
        </div>
      </nav>

      <div className="cshell-body">
        <header className="cshell-topbar">
          <Link to={CONSOLE_HOME} className="cshell-topbar-brand">
            {BRAND_NAME}
          </Link>
          <NavLink
            to="/inbox"
            className={({ isActive }) => `cshell-icon-btn${isActive ? ' is-active' : ''}`}
            aria-label={unread > 0 ? `消息，${unread} 条未读` : '消息'}
          >
            <BellOutlined />
            {unread > 0 ? (
              <span className="cshell-badge" aria-hidden="true">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) => `cshell-icon-btn${isActive ? ' is-active' : ''}`}
            aria-label="帮助"
          >
            <QuestionCircleOutlined />
          </NavLink>
          <TenantSwitch />
          <UserMenu />
        </header>

        <main className="cshell-main">
          <Outlet />
        </main>
      </div>

      <nav className="cshell-tabbar" aria-label="底部导航">
        {main.map((item) => (
          <TabLink key={item.to} item={item} />
        ))}
        <MoreTab gear={gear} unread={unread} />
      </nav>
    </div>
  );
}
